import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { Download, RefreshCw, DollarSign } from "lucide-react";
import reportsService from "@/services/reportsService";
import { SubsidiaryFilter, DateRangeFilter } from "../filters";

/**
 * Cash Flow Statement Component
 *
 * Displays cash flows from operating, investing, and financing activities.
 *
 * Requirements: 8.1-8.9
 */
const CashFlowStatement = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statement, setStatement] = useState(null);
  const [filters, setFilters] = useState({
    subsidiary_id: "",
    date_from: "",
    date_to: "",
  });

  useEffect(() => {
    if (filters.date_from && filters.date_to) {
      loadStatement();
    }
  }, []);

  const loadStatement = async () => {
    if (!filters.date_from || !filters.date_to) {
      return;
    }

    try {
      setLoading(true);
      const response = await reportsService.getCashFlow(filters);

      if (response?.data?.success) {
        setStatement(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load cash flow statement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadStatement();
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: statement,
        reportType: "cash_flow",
        reportTitle: `Cash Flow Statement (${filters.date_from} to ${filters.date_to})`,
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Cash Flow Statement
          </h1>
          <p className="text-gray-600 mt-1">
            Statement of cash flows from operating, investing, and financing
            activities
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SubsidiaryFilter
              value={filters.subsidiary_id}
              onChange={(value) =>
                setFilters({ ...filters, subsidiary_id: value })
              }
            />
            <DateRangeFilter
              dateFrom={filters.date_from}
              dateTo={filters.date_to}
              onDateFromChange={(value) =>
                setFilters({ ...filters, date_from: value })
              }
              onDateToChange={(value) =>
                setFilters({ ...filters, date_to: value })
              }
            />
          </div>
          <div className="mt-4">
            <Button onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Generate Statement"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statement */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Generating statement...</p>
          </CardContent>
        </Card>
      ) : statement ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Cash Flow Statement
              {statement.period && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  {statement.period.date_from} to {statement.period.date_to}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableBody>
                  {/* Operating Activities */}
                  <TableRow className="bg-blue-50 font-semibold">
                    <TableCell colSpan={2}>
                      CASH FLOWS FROM OPERATING ACTIVITIES
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-semibold border-t">
                    <TableCell>Net Cash from Operating Activities</TableCell>
                    <TableCell
                      className={`text-right ${
                        statement.operating_activities >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(statement.operating_activities)}
                    </TableCell>
                  </TableRow>

                  {/* Investing Activities */}
                  <TableRow className="bg-purple-50 font-semibold">
                    <TableCell colSpan={2}>
                      CASH FLOWS FROM INVESTING ACTIVITIES
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-semibold border-t">
                    <TableCell>Net Cash from Investing Activities</TableCell>
                    <TableCell
                      className={`text-right ${
                        statement.investing_activities >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(statement.investing_activities)}
                    </TableCell>
                  </TableRow>

                  {/* Financing Activities */}
                  <TableRow className="bg-orange-50 font-semibold">
                    <TableCell colSpan={2}>
                      CASH FLOWS FROM FINANCING ACTIVITIES
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-semibold border-t">
                    <TableCell>Net Cash from Financing Activities</TableCell>
                    <TableCell
                      className={`text-right ${
                        statement.financing_activities >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(statement.financing_activities)}
                    </TableCell>
                  </TableRow>

                  {/* Net Change in Cash */}
                  <TableRow className="bg-gray-100 font-bold text-lg border-t-2">
                    <TableCell>NET CHANGE IN CASH</TableCell>
                    <TableCell
                      className={`text-right ${
                        statement.net_cash_flow >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(statement.net_cash_flow)}
                    </TableCell>
                  </TableRow>

                  {/* Cash Balances */}
                  <TableRow className="border-t">
                    <TableCell className="font-semibold">
                      Cash at Beginning of Period
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.opening_cash_balance)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-semibold">
                      Cash at End of Period
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(statement.closing_cash_balance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Cash Flow Summary */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Operating Cash Flow</p>
                <p
                  className={`text-xl font-bold ${
                    statement.operating_activities >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(statement.operating_activities)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Investing Cash Flow</p>
                <p
                  className={`text-xl font-bold ${
                    statement.investing_activities >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(statement.investing_activities)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Financing Cash Flow</p>
                <p
                  className={`text-xl font-bold ${
                    statement.financing_activities >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(statement.financing_activities)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Net Change</p>
                <p
                  className={`text-xl font-bold ${
                    statement.net_cash_flow >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(statement.net_cash_flow)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            Select a date range and click "Generate Statement" to view the cash
            flow statement.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CashFlowStatement;
